/**
 * Grupos por día para listas cronológicas (timeline de Leads, Historial):
 * «Hoy», «Ayer», «Esta semana», «Semana pasada» o el mes («Septiembre»,
 * «Septiembre 2025» si es de otro año). Hora local del navegador.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const toDate = (value) => (value instanceof Date ? value : new Date(value));

/**
 * Rótulo del grupo de día de una fecha.
 *
 * @param {string|number|Date} value
 * @param {Date} [now]
 * @returns {string}
 */
export function getDayGroupLabel(value, now = new Date()) {
  const dt = toDate(value);
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(dt.getTime()) || Number.isNaN(current.getTime())) return 'Sin fecha';

  const day = startOfLocalDay(dt);
  const today = startOfLocalDay(current);
  const diffDays = Math.round((today.getTime() - day.getTime()) / DAY_MS);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';

  const daysSinceMonday = (today.getDay() + 6) % 7;
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - daysSinceMonday);
  if (diffDays > 1 && day >= startOfWeek) return 'Esta semana';

  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfWeek.getDate() - 7);
  if (day >= startOfLastWeek && day < startOfWeek) return 'Semana pasada';

  const month = dt.toLocaleDateString('es-AR', { month: 'long' });
  const label = month.charAt(0).toUpperCase() + month.slice(1);
  return dt.getFullYear() === current.getFullYear() ? label : `${label} ${dt.getFullYear()}`;
}

/**
 * Más nuevo arriba, partido en grupos de día consecutivos.
 *
 * @template T
 * @param {T[]} entries
 * @param {(entry: T) => (string|number|Date|null|undefined)} getValue fecha de cada entrada
 * @param {Date} [now]
 * @returns {{ label: string, entries: T[] }[]}
 */
export function groupByDay(entries, getValue, now = new Date()) {
  const list = Array.isArray(entries) ? entries : [];
  const ts = (entry) => {
    const t = new Date(getValue(entry)).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  const sorted = list.slice().sort((a, b) => ts(b) - ts(a));
  const groups = [];
  sorted.forEach((entry) => {
    const label = getDayGroupLabel(getValue(entry), now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.entries.push(entry);
    else groups.push({ label, entries: [entry] });
  });
  return groups;
}

/**
 * Hora de una entrada dentro de su grupo: «Hoy» y «Ayer» ya dicen el día, así
 * que alcanza la hora («14:32»); en el resto va día/mes + hora («15/10 14:32»).
 *
 * @param {string|number|Date} value
 * @param {string} groupLabel
 * @returns {string}
 */
export function formatDayGroupStamp(value, groupLabel) {
  if (value == null || value === '') return '';
  const dt = toDate(value);
  if (Number.isNaN(dt.getTime())) return '';
  const hm = dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (groupLabel === 'Hoy' || groupLabel === 'Ayer') return hm;
  const day = dt.toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });
  return `${day} ${hm}`;
}
